import { createSlice, current } from "@reduxjs/toolkit";
import { api } from "../../api/httpclient";
import {
  canViewAnySchedule,
  canCreateSchedule,
  canEditSchedule,
  canDeleteSchedule,
} from "../../routing/authorizationhandlers";
import { addNotification } from "./notificationsslice";
import {
  collectParametersFromRules,
  getAlertCoveredPeriodIntervals,
  getAlertImportances,
  getAlertRecurrsEveryIntervals,
  getAlertVariantCoveredPeriodIntervals,
  getComparisonOperators,
  notificationDurationMiddle,
} from "../../lib/utils";
import { AggregateFunction } from "../../lib/aggregatefunctions";
import { formatQuery } from "react-querybuilder";

const defaultDataset = {
  Id: 0,
  Title: "None",
  Description: "",
  CategoryId: 0,
  Alerts: [],
  Fields: [],
  Users: [],
  Where: [],
  OrderId: -1,
};

const defaultDatasetId = 0;

const defaultFormInitialValues = {
  Id: 0,
  Name: "",
  Description: "",
  ConnectorConfigId: "",
  Cron: "",
  GoogleSpreadSheetId: 0,
  IsActive: false,
  Jobs: [],
  GoogleList: [],
  Connections: [],
  Source: "",
  SourceId: 0,
  RecurrencyFrom: "",
  RecurrsEvery: 0,
  Parameters: "",
  ReportId: 0,
  StartImmediately: false,
  Type: "",
  TypeId: 0,
  WhereObject: "",
  WhereParameters: "",
  WhereStatement: "",
  WidgetsIds: "",
  ColumnNames: [],
  CustomFields: [{ index: 1, field: "", value: "" }],
  DatasetId: defaultDatasetId,
  ShowGoogleSheets: false,
  ReportTitle: "",
  Widgets: [],
  WidgetTitle: "",

  entityFilter: "",
  entitySysFilter: "",

  generalOpen: true,
  recurrsOpen: true,
  filterOpen: false,
};

const initialState = {
  pageLoaded: false,
  pageDisposed: true,
  processing: false,
  processingText: "",
  mode: 0,
  canView: false,
  canCreate: false,
  canEdit: false,
  canDelete: false,

  entities: [],

  formKey: new Date().toString(),
  formInitialValues: { ...defaultFormInitialValues },
  formValues: { ...defaultFormInitialValues },
  formClientErrors: {},
  formServerErrors: [],
  currentDataset: defaultDataset,

  toolsVisible: true,
  panes: [
    {
      size: "20%",
      min: "20%",
      max: "25%",
      collapsible: false,
    },
    {
      min: "80%",
      max: "80%",
      collapsible: false,
    },
  ],
};

const scheduleViewPageSlice = createSlice({
  name: "scheduleViewPage",
  initialState: initialState,
  reducers: {
    setInitialState: (state, action) => ({ ...initialState }),

    setProcessing: (state, action) => {
      const { processing = true, processingText = "" } = action.payload;

      return {
        ...state,
        processing: processing,
        processingText: processing == false ? "" : processingText,
      };
    },

    setPanes: (state, action) => ({ ...state, panes: { ...action.payload } }),

    setEntityFilter: (state, action) => ({
      ...state,
      entityFilter: action.payload,
    }),
    setEntitySysFilter: (state, action) => ({
      ...state,
      entitySysFilter: action.payload,
    }),
    setPageValue: (state, action) => ({
      ...state,
      [action.payload.id]: action.payload.value,
    }),

    setFormValue: (state, action) => {
      //payload: {id: fieldId, value: fieldValue}
      let id = action.payload.id;
      let value = action.payload.value;

      return {
        ...state,
        formValues: { ...state.formValues, [id]: value },
      };
    }, //payload: {id: , value:}

    setConnectionFormValue: (state, action) => {
      let id = action.payload.id;
      let value = action.payload.value;

      return {
        ...state,
        formValues: {
          ...state.formValues,
          DbConnection: {
            ...state.formValues.DbConnection,
            [id]: value,
          },
        },
      };
    },

    setFormClientErrors: (state, action) => ({
      ...state,
      formClientErrors: { ...action.payload },
    }),

    setFormServerErrors: (state, action) => ({
      ...state,
      formServerErrors: [...action.payload],
    }),

    pageLoaded: (state, action) => {
      const pageState = current(state);

      if (pageState.pageDisposed) return state;

      return {
        ...state,
        pageLoaded: true,
        processing: false,
        processingText: "",
        formClientErrors: {},
        formServerErrors: [],
        entities: [
          ...(action.payload.Exports ?? []).map((item) => ({
            ...item,
            importexport: "export",
          })),
          ...(action.payload.Imports ?? []).map((item) => ({
            ...item,
            importexport: "import",
          })),
        ],
        canView: canViewAnySchedule(action.payload.currentUser),
        canCreate: canCreateSchedule(action.payload.currentUser),
        canEdit: canEditSchedule(action.payload.currentUser),
        canDelete: canDeleteSchedule(action.payload.currentUser),
        currentDataset: defaultDataset,
      };
    },

    entityLoaded: (state, action) => {
      const pageState = current(state);
      if (pageState.pageDisposed) return state;
      if (action.payload.importexport == "export") {
        let i = 1;
        const customFields = action.payload.data.CustomFields.map((w) => ({
          index: i++,
          field: w.FieldKey, // take the 'field' property
          value: w.FieldValue, // take the 'value' property
        }));

        return {
          ...state,
          pageLoaded: true,
          processing: false,
          processingText: "",
          formKey: new Date().toString(),
          formInitialValues: {
            ...state.formValues,
            ...action.payload.data,
            Id: action.payload.Id,
            Name: action.payload.data.Name,
            Description: action.payload.data.Description,
            RecurrencyFrom:
              action.payload.data.Cron == "" ? "fromRecurrency" : "fromCron",
            RecurrsEvery: action.payload.data.RecurrsEvery,
            ConnectorConfigId: action.payload.data.ConnectorConfigId,
            Cron: action.payload.data.Cron,
            GoogleSpreadSheetId: action.payload.data.GoogleSpreadSheetId,
            IsActive: action.payload.data.IsActive,
            Jobs: action.payload.data.Jobs,
            Source: action.payload.data.Source,
            SourceId: action.payload.data.SourceId,
            RecurrsEvery: action.payload.data.RecurrsEvery,
            Parameters: action.payload.data.Parameters,
            ReportId: action.payload.data.ReportId,
            StartImmediately: action.payload.data.StartImmediately,
            Type: action.payload.data.Type,
            TypeId: action.payload.data.TypeId,
            WhereParameters: action.payload.data.Parameters,
            WhereStatement: action.payload.data.WhereStatement,
            WidgetsIds: action.payload.data.WidgetsIds,
            DatasetId: action.payload.data.DatasetId,
            ReportTitle: action.payload.data.ReportTitle,
            Widgets: action.payload.data.Widgets,
            WhereObject: JSON.parse(action.payload.data.WhereObject),
            CustomFields: customFields,
            importexport: action.payload.importexport,
          },
          formValues: {
            ...state.formValues,
            ...action.payload.data,
            Id: action.payload.Id,
            Name: action.payload.data.Name,
            Description: action.payload.data.Description,
            RecurrsEvery: action.payload.data.RecurrsEvery,
            RecurrencyFrom:
              action.payload.data.Cron == "" ? "fromRecurrency" : "fromCron",
            ConnectorConfigId: action.payload.data.ConnectorConfigId,
            Cron: action.payload.data.Cron,
            GoogleSpreadSheetId: action.payload.data.GoogleSpreadSheetId,
            IsActive: action.payload.data.IsActive,
            Jobs: action.payload.data.Jobs,
            Source: action.payload.data.Source,
            SourceId: action.payload.data.SourceId,
            RecurrsEvery: action.payload.data.RecurrsEvery,
            Parameters: action.payload.data.Parameters,
            ReportId: action.payload.data.ReportId,
            StartImmediately: action.payload.data.StartImmediately,
            Type: action.payload.data.Type,
            TypeId: action.payload.data.TypeId,
            WhereParameters: action.payload.data.Parameters,
            WhereStatement: action.payload.data.WhereStatement,
            WidgetsIds: action.payload.data.WidgetsIds,
            DatasetId: action.payload.data.DatasetId,
            ReportTitle: action.payload.data.ReportTitle,
            Widgets: action.payload.data.Widgets,
            WhereObject: JSON.parse(action.payload.data.WhereObject),
            CustomFields: customFields,
            importexport: action.payload.importexport,
          },
          formClientErrors: {},
          formServerErrors: [],
          currentDataset: defaultDataset,
        };
      }

      if (action.payload.importexport == "import") {
        return {
          ...state,
          pageLoaded: true,
          processing: false,
          processingText: "",
          formKey: new Date().toString(),
          formInitialValues: {
            ...state.formValues,
            ...action.payload.data,
            Id: action.payload.Id,
            Name: action.payload.data.Name,
            Description: action.payload.data.Description,
            RecurrencyFrom:
              action.payload.data.Cron == "" ? "fromRecurrency" : "fromCron",
            RecurrsEvery: action.payload.data.RecurrsEvery,
            ConnectorConfigId: action.payload.data.ConnectorConfigId,
            Cron: action.payload.data.Cron,
            GoogleSpreadSheetId: action.payload.data.GoogleSpreadSheetId,
            IsActive: action.payload.data.IsActive,
            Jobs: action.payload.data.Jobs,
            Source: action.payload.data.Source,
            SourceId: action.payload.data.SourceId,
            RecurrsEvery: action.payload.data.RecurrsEvery,
            StartImmediately: action.payload.data.StartImmediately,
            Type: action.payload.data.Type,
            TypeId: action.payload.data.TypeId,
            DatasetId: action.payload.data.DatasetId,
            importexport: action.payload.importexport,
          },
          formValues: {
            ...state.formValues,
            ...action.payload.data,
            Id: action.payload.Id,
            Name: action.payload.data.Name,
            Description: action.payload.data.Description,
            RecurrsEvery: action.payload.data.RecurrsEvery,
            RecurrencyFrom:
              action.payload.data.Cron == "" ? "fromRecurrency" : "fromCron",
            ConnectorConfigId: action.payload.data.ConnectorConfigId,
            Cron: action.payload.data.Cron,
            GoogleSpreadSheetId: action.payload.data.GoogleSpreadSheetId,
            IsActive: action.payload.data.IsActive,
            Jobs: action.payload.data.Jobs,
            Source: action.payload.data.Source,
            SourceId: action.payload.data.SourceId,
            RecurrsEvery: action.payload.data.RecurrsEvery,
            StartImmediately: action.payload.data.StartImmediately,
            Type: action.payload.data.Type,
            TypeId: action.payload.data.TypeId,
            DatasetId: action.payload.data.DatasetId,
            importexport: action.payload.importexport,
          },
          formClientErrors: {},
          formServerErrors: [],
          currentDataset: defaultDataset,
        };
      }
    },

    entityUpdated: (state, action) => {
      const pageState = current(state);

      if (pageState.pageDisposed) return state;

      const entity = pageState.formValues;

      //reflect on entities.name

      return {
        ...state,
        formKey: new Date().toString(),
        formInitialValues: { ...state.formValues },
        formClientErrors: {},
        formServerErrors: [],
      };
    },

    entityDeleted: (state, action) => {
      //payload: the entity referenceKey
      let pageState = current(state);

      if (pageState.pageDisposed) return state;
      let entityReferenceKey = action.payload;
      let newItems = pageState.entities.filter(
        (e) => e.Id != entityReferenceKey,
      );

      return {
        ...state,
        entities: newItems,
      };
    },

    removeEntityFromUI: (state, action) => {
      //payload: the entity id
      let pageState = current(state);
      let entityId = action.payload;

      //remove from entities
      let entities = pageState.entities.filter((e) => e.Id != entityId);

      //if it's the current entity => goto no mode
      let currentMode = {};
      if (pageState.mode == 2 && pageState.formValues.Id == entityId)
        currentMode = {
          mode: 0,
          formKey: new Date().toString(),
          formInitialValues: { ...defaultFormInitialValues },
          formValues: { ...defaultFormInitialValues },
          formClientErrors: {},
          formServerErrors: [],
        };

      return { ...state, entities: entities, ...currentMode };
    },

    setPageJobColelction: (state, action) => {
      let pageState = current(state);
      return {
        ...state,
        pageLoaded: true,
        processing: false,
        processingText: "",
        formValues: {
          ...state.formValues,
          JobCollection: action.payload,
        },
      };
    },
    showHideGoogleSheets: (state, action) => {
      return {
        ...state,
        formValues: {
          ...state.formValues,
          ShowGoogleSheets: action.payload,
        },
      };
    },
    datasetLoaded: (state, action) => {
      const pageState = current(state);

      if (pageState.pageDisposed) return state;

      let currentDataset = pageState.currentDataset;

      //aggregate expressions - already sorted by title by api
      let aggregateExpressions = action.payload.fields
        .filter(
          (e) =>
            e.IsExpression == true &&
            e.IsAggregate == true &&
            e.DataTypeId == 2,
        )
        .map((item) => ({ Id: item.Id, Title: item.Title, Name: item.Name }));
      //aggregateExpressions = sortArrayByString(aggregateExpressions, "Title");
      aggregateExpressions = [
        { Id: null, Title: "None", Name: "None" },
        ...aggregateExpressions,
      ];

      //fields - already sorted by title by api
      //let fields = action.payload.fields.filter(e => (e.IsExpression == false && e.IsAggregate==false && e.DataTypeId == 2)).map(item => ({Id: item.Id, Title: item.Title, Name: item.Name}));
      let fields = action.payload.fields
        .filter((e) => e.IsExpression == false && e.IsAggregate == false)
        .map((item) => ({
          Id: item.Id,
          Title: item.Title,
          Name: item.Name,
          DataTypeId: item.DataTypeId,
        }));
      //fields = sortArrayByString(fields, "Title");
      fields = [
        { Id: null, Title: "None", Name: "None", DataTypeId: 0 },
        ...fields,
      ];

      //time fields - already sorted by title by api
      let timeFields = action.payload.fields
        .filter(
          (e) =>
            e.IsExpression == false &&
            e.IsAggregate == false &&
            e.DataTypeId == 5 &&
            e.IsUTC == true,
        )
        .map((item) => ({ Id: item.Id, Title: item.Title, Name: item.Name }));
      //timeFields = sortArrayByString(timeFields, "Title");
      timeFields = [{ Id: 0, Title: "None", Name: "None" }, ...timeFields];

      //users - already sorted by name by api
      let users = action.payload.users.map((item) => ({
        Id: item.Id,
        Name: item.Name,
        IsActive: item.IsActive,
        Checked: false,
      }));
      //users = sortArrayByString(users, "Name");

      //groups - already sorted by name by api
      let groups = action.payload.groups.map((item) => ({
        Id: item.Id,
        Name: item.Name,
        Checked: false,
      }));

      //groupby fields - fields excluding is expression and excluding is aggregate and those that are not groupable in alert
      let groupByFields = action.payload.fields
        .filter((e) => e.IsAggregate == false && e.IsGroupableInAlert == true)
        .map((item) => ({
          Id: item.Id,
          Title: item.Title,
          Name: item.Name,
          Checked: false,
        }));
      //let groupByFields = action.payload.fields.filter(e => (e.IsExpression == false && e.IsAggregate==false && e.IsGroupableInAlert == true)).map(item => ({Id: item.Id, Title: item.Title, Name: item.Name, Checked: false}));

      //where
      let where = action.payload.where;
      for (let prop in where) {
        //must add name and label (lowercases) to every field
        where[prop].name = where[prop].Name;
        where[prop].label = where[prop].Title;

        if (where[prop].DataTypeId == 1) {
          where[prop].inputType = "bool";
          //where[prop].dataType="bool";
        } else if (where[prop].DataTypeId == 2) {
          where[prop].inputType = "number";
          //where[prop].dataType="number";
        } else if (where[prop].DataTypeId == 3) {
          where[prop].inputType = "string";
          //where[prop].dataType="string";
        } else if (where[prop].DataTypeId == 4 || where[prop].DataTypeId == 5) {
          where[prop].inputType = "date";
        }

        if (where[prop].IsLookup) {
          for (let p in where[prop].values) {
            where[prop].values[p].label = where[prop].values[p].Title;
          }
        }
      }

      return {
        ...state,
        processing: false,
        processingText: "",
        currentDataset: {
          ...currentDataset,
          Fields: [...action.payload.fields],
          Users: [...action.payload.users],
          Groups: [...action.payload.groups],
          Where: where,
          Lookups: {
            AggregateExpressions: aggregateExpressions,
            Fields: fields,
            AggregateFunctions: [
              { Id: 0, Title: "None", Name: "None" },
              ...AggregateFunction.functions
                .sort((a, b) =>
                  a.title < b.title ? -1 : a.title == b.title ? 0 : 1,
                )
                .map((e) => ({
                  Id: e.id,
                  Title: e.title,
                  Name: e.name,
                  Description: e.description,
                  AppliesTo: e.appliesTo,
                })),
            ], //getAggregateFunctions(),
            Operators: getComparisonOperators(),
            AlertImportances: getAlertImportances(),
            AlertRecurrsEveryIntervals: getAlertRecurrsEveryIntervals(),
            AlertCoveredPeriodIntervals: getAlertCoveredPeriodIntervals(),
            AlertVariantCoveredPeriodIntervals:
              getAlertVariantCoveredPeriodIntervals(),
            TimeFields: timeFields,
            Users: users,
            Groups: groups,
            GroupByFields: groupByFields,
          },
        },
        formKey: new Date().toString(),
        formInitialValues: { ...defaultFormInitialValues },
        formValues: {
          ...state.formValues,
          DatasetId: action.payload.datasetId,
          Groups: groups,
          GroupedFields: groupByFields,
          Fields: [],
        },
        formServerErrors: [],
      };
    },

    datasetsLoaded: (state, action) => {
      const pageState = current(state);

      if (pageState.pageDisposed) return state;
      return {
        ...state,
        processing: false,
        processingText: "",
        formKey: new Date().toString(),
        formValues: {
          ...state.formValues,
          Datasets: action.payload.datasets,
        },
        formServerErrors: [],
      };
    },

    disposePage: (state, action) => {
      //payload: bool
      if (action.payload) return { ...initialState };
      else return { ...state, pageDisposed: false };
    },
  },
});

export default scheduleViewPageSlice.reducer;

export const loadPage = (t) => async (dispatch, getState) => {
  //start with initial values
  dispatch(setInitialState());

  //undispose page
  dispatch(disposePage(false));

  //show loader
  dispatch(
    setProcessing({
      processing: true,
      processingText: t("slices.loadingPage"),
    }),
  );

  try {
    var response = await api({
      url: "scheduleexport/getscheduleexportimportlist",
      method: "post",
      data: {},
    });

    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().scheduleViewPage.pageDisposed) return;

    //it's ok to dispatch here
    let data = response.data.Data;

    dispatch(pageLoaded(data));

    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const loadEntity = (id, type, t) => async (dispatch, getState) => {
  const pageState = getState().scheduleViewPage;

  //show loader
  dispatch(
    setProcessing({
      processing: true,
      processingText: t("slices.loadingPage"),
    }),
  );

  const url =
    type === "export"
      ? "scheduleexport/getscheduleexportbyid"
      : type === "import"
        ? "scheduleimport/getscheduleimportlistbyid"
        : "";

  try {
    var response = await api({
      url: url,
      method: "post",
      data: {
        id: Number(id),
      },
      //headers:{'h':'1'}
    });

    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().scheduleViewPage.pageDisposed) return;

    //it's ok to dispatch here
    let data = response.data.Data;

    //transform the response
    dispatch(entityLoaded({ data: data, importexport: type }));

    //console.log("AFTER API STATE IS ", getState().reportingViewPage)
    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const saveEntity = (type, t) => async (dispatch, getState) => {
  const pageState = getState().scheduleViewPage;
  //show loader
  dispatch(
    setProcessing({
      processing: true,
      processingText: t("slices.updatingDataset"),
    }),
  );
  let data = null;

  if (type === "export") {
    const customFields = pageState.formValues.CustomFields.map((w) => ({
      FieldKey: w.field, // take the 'field' property
      FieldValue: w.value, // take the 'value' property
    }));

    const widgetId = JSON.parse(pageState.formValues.WidgetsIds);

    //some tramsformation is needed
    let whereObject = JSON.stringify(pageState.formValues.WhereObject);
    let whereStatement = "";
    let whereParameters = [];

    // generate parameterized SQL
    const formatted = formatQuery(
      pageState.formValues.WhereObject,
      "parameterized",
    );

    whereParameters =
      pageState.formValues.WhereObject?.rules?.length > 0
        ? collectParametersFromRules(pageState.formValues.WhereObject.rules)
        : [];

    if (
      (whereParameters != null && whereParameters.length > 0) ||
      (pageState.formValues.WhereObject.rules &&
        pageState.formValues.WhereObject.rules.length > 0)
    ) {
      whereStatement = formatted.sql;
    }

    let widgets = pageState.formValues.Widgets;
    if (widgetId) {
      widgets = pageState.formValues.Widgets.filter((w) => w.Id == widgetId);
    }

    const finalWidgets = widgets.map((w) => ({
      RequestId: "",
      DataSourceId: w.DataSourceId,
      WhereStatement: whereStatement,
      WhereParameters: whereParameters ?? [],
      Parameters: [],
      WhereObject: whereObject,
      WidgetsIds: JSON.parse(pageState.formValues.WidgetsIds).flat(),
      MaximumRows: w.Settings.MaximumRows,
    }));

    data = {
      Id: Number(pageState.formValues.Id),
      Name: pageState.formValues.Name,
      Description: pageState.formValues.Description,
      TypeId: pageState.formValues.TypeId,
      SourceId: pageState.formValues.SourceId,
      RecursEvery: pageState.formValues.RecursEvery,
      ReportId: pageState.formValues.ReportId,
      ConnectorConfigId: pageState.formValues.ConnectorConfigId,
      GoogleSpreadSheetId: pageState.formValues.GoogleSpreadSheetId,
      Cron: pageState.formValues.Cron,
      StartImmediately: false,
      Widgets: finalWidgets,
      CustomFields: customFields,
    };
  }

  if (type === "import") {
    data = {
      Id: Number(pageState.formValues.Id),
      Name: pageState.formValues.Name,
      Description: pageState.formValues.Description,
      TypeId: pageState.formValues.TypeId,
      SourceId: pageState.formValues.SourceId,
      RecursEvery: pageState.formValues.RecursEvery,
      DatasetId: pageState.formValues.DatasetId,
      ConnectorConfigId: pageState.formValues.ConnectorConfigId,
      GoogleSpreadSheetId: pageState.formValues.GoogleSpreadSheetId,
      Cron: pageState.formValues.Cron,
      StartImmediately: false,
      IdentityColumn: pageState.formValues.IdentityColumn,
    };
  }

  const url =
    type === "export"
      ? "scheduleexport/updatescheduleexport"
      : type === "import"
        ? "scheduleimport/updatescheduleimport"
        : "";

  try {
    var response = await api({
      url: url,
      method: "post",
      data: data,
      //headers:{'h':'1'}
    });
    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().scheduleViewPage.pageDisposed) return;

    const res = response.data.Data;

    dispatch(entityUpdated());

    dispatch(setProcessing({ processing: false }));

    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const deleteEntity = (id, type, t) => async (dispatch, getState) => {
  //payload the entity id
  dispatch(
    setProcessing({
      processing: true,
      processingText: t("slices.deletingScheduleReport"),
    }),
  );

  const url =
    type === "export"
      ? "scheduleexport/deletescheduleexport"
      : type === "import"
        ? "scheduleimport/deletescheduleimport"
        : "";

  try {
    var response = await api({
      url: url,
      method: "post",
      data: { id: id },
      //headers:{'h':'1'}
    });

    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().scheduleViewPage.pageDisposed) return;

    let data = response.data.Data;

    dispatch(entityDeleted(id));

    dispatch(setProcessing({ processing: false }));

    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const pauseSchedule = (id, type, t) => async (dispatch, getState) => {
  //payload the entity id
  dispatch(
    setProcessing({
      processing: true,
      processingText: t("slices.loadingPage"),
    }),
  );

  const url =
    type === "export"
      ? "scheduleexport/pausescheduleexportjob"
      : type === "import"
        ? "scheduleimport/pausescheduleimportjob"
        : "";

  try {
    var response = await api({
      url: url,
      method: "post",
      data: { Id: id },
      //headers:{'h':'1'}
    });

    dispatch(setProcessing({ processing: false }));

    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const resumeSchedule = (id, type, t) => async (dispatch, getState) => {
  //payload the entity id
  dispatch(
    setProcessing({
      processing: true,
      processingText: t("slices.loadingPage"),
    }),
  );

  const url =
    type === "export"
      ? "scheduleexport/resumescheduleexportjob"
      : type === "import"
        ? "scheduleimport/resumescheduleimportjob"
        : "";

  try {
    var response = await api({
      url: url,
      method: "post",
      data: { Id: id },
    });

    dispatch(setProcessing({ processing: false }));

    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const loadDataset = (datasetId, t) => async (dispatch, getState) => {
  //show loading
  dispatch(
    setProcessing({
      processing: true,
      processingText: t("slices.loadingPage"),
    }),
  );

  if (datasetId == 0) {
    dispatch(
      datasetLoaded({
        datasetId: 0,
        fields: [],
        users: [],
        where: [],
        groups: [],
      }),
    );
    return;
  }

  try {
    var response = await api({
      url: "datacorrection/getdatacorrectionbydatasetid",
      method: "post",
      data: {
        datasetId: datasetId,
      },
    });

    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().scheduleViewPage.pageDisposed) return;

    let data = response.data.Data;

    dispatch(
      datasetLoaded({
        datasetId: datasetId,
        fields: data.Fields,
        users: data.Users,
        groups: data.SecurityGroups,
        where: data.Where,
      }),
    );

    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const loadDatasets = (t) => async (dispatch, getState) => {
  //show loader
  dispatch(
    setProcessing({
      processing: true,
      processingText: t("slices.loadingPage"),
    }),
  );

  try {
    var response = await api({
      url: "dataset/getuserdefinedwithsystemdatasetlist",
      method: "post",
      data: {},
      //headers:{'h':'1'}
    });

    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().scheduleViewPage.pageDisposed) return;

    let data = response.data.Data;

    dispatch(datasetsLoaded({ datasets: data.Items }));

    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const loadGoogleSheets =
  (connectionId, t) => async (dispatch, getState) => {
    //show loader
    dispatch(
      setProcessing({
        processing: true,
        processingText: t("slices.loadingPage"),
      }),
    );
    try {
      const response = await api({
        url: "google/getgoogledrivefileslist",
        method: "post",
        data: { ConnectionId: connectionId },
      });

      //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
      if (getState().scheduleViewPage.pageDisposed) return;
      //it's ok to dispatch here
      let data = response.data.Data;

      //transform the response
      dispatch(setFormValue({ id: "GoogleList", value: data.Files }));
      dispatch(setProcessing({ processing: false, processingText: "" }));

      return Promise.resolve(response);
    } catch (err) {
      //i am here => could be any error included 200 and Succeeded=false
      return Promise.reject(err);
    }
  };

export const loadGoogleSheetId =
  (SpreadSheetId, SheetName, SpreadSheetPath, ConnectionId, t) =>
  async (dispatch, getState) => {
    //show loader
    // dispatch(
    //   setProcessing({
    //     processing: true,
    //     processingText: t("slices.loadingPage"),
    //   })
    // );
    try {
      const response = await api({
        url: "google/getgooglesheetid",
        method: "post",
        data: {
          SpreadSheetId: SpreadSheetId,
          SheetName: SheetName,
          SpreadSheetPath: SpreadSheetPath,
          ConnectionId: ConnectionId,
        },
      });

      //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
      if (getState().scheduleViewPage.pageDisposed) return;
      //it's ok to dispatch here
      let data = response.data.Data;
      dispatch(setFormValue({ id: "GoogleSheetId", value: data.Id }));
      dispatch(setFormValue({ id: "ColumnNames", value: data.ColumnNames }));

      //transform the response
      //dispatch(setProcessing({ processing: false, processingText: "" }));

      return Promise.resolve(response);
    } catch (err) {
      //i am here => could be any error included 200 and Succeeded=false
      return Promise.reject(err);
    }
  };

export const getGoogleAuth = (t) => async (dispatch, getState) => {
  //show loader
  dispatch(
    setProcessing({
      processing: true,
      processingText: t("slices.loadingPage"),
    }),
  );
  try {
    const response = await api({
      url: "google/getgoogleauthurl",
      method: "post",
      data: {},
    });

    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().scheduleViewPage.pageDisposed) return;

    //it's ok to dispatch here
    const { RedirectUrl } = response.data.Data;

    if (RedirectUrl) {
      window.open(RedirectUrl, "_blank", "noopener,noreferrer");
    }

    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const getConnectionsList = (userId, t) => async (dispatch, getState) => {
  //show loader
  dispatch(
    setProcessing({
      processing: true,
      processingText: t("slices.loadingPage"),
    }),
  );
  try {
    const response = await api({
      url: "connector/getconnections",
      method: "post",
      data: { userId: userId },
    });

    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().scheduleViewPage.pageDisposed) return;
    //it's ok to dispatch here
    let data = response.data.Data;

    //transform the response
    dispatch(setFormValue({ id: "Connections", value: data.Connections }));

    dispatch(setProcessing({ processing: false, processingText: "" }));

    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const {
  setInitialState,
  setProcessing,
  setPageValue,
  setFormValue,
  setConnectionFormValue,
  setFormClientErrors,
  setFormServerErrors,
  entityDeleted,
  setPanes,
  pageLoaded,
  entityLoaded,
  entityUpdated,
  removeEntityFromUI,
  setEntityFilter,
  setEntitySysFilter,
  setPageJobColelction,
  datasetLoaded,
  showHideGoogleSheets,
  datasetsLoaded,
  disposePage,
} = scheduleViewPageSlice.actions;
